import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, X, Eye, EyeOff, KeyRound, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function UtentiTab() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState({}); // {account_id: [user_id, ...]}
  const [savingKey, setSavingKey] = useState(null); // `${user_id}-${account_id}` in flight
  // Password change
  const [pwUser, setPwUser] = useState('ufficio');
  const [pwNew, setPwNew] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/permissions`);
      setAccounts(res.data.accounts || []);
      setUsers(res.data.users || []);
      setMatrix(res.data.matrix || {});
    } catch (e) {
      toast.error('Errore caricamento permessi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isAllowed = (userId, accountId) =>
    (matrix[accountId] || []).includes(userId);

  const togglePerm = async (userId, accountId) => {
    const key = `${userId}-${accountId}`;
    const newAllowed = !isAllowed(userId, accountId);
    setSavingKey(key);
    try {
      await axios.post(`${API}/admin/permissions`, {
        user_id: userId,
        account_id: accountId,
        allowed: newAllowed,
      });
      setMatrix(prev => {
        const next = { ...prev };
        const cur = next[accountId] || [];
        next[accountId] = newAllowed
          ? [...cur, userId]
          : cur.filter(u => u !== userId);
        return next;
      });
      toast.success(newAllowed ? 'Accesso abilitato' : 'Accesso revocato');
    } catch {
      toast.error('Errore aggiornamento permesso');
    } finally {
      setSavingKey(null);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    if (!pwNew || pwNew.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/admin/users/password`, {
        username: pwUser,
        new_password: pwNew,
      });
      toast.success(`Password di "${pwUser}" aggiornata`);
      setPwNew('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento password');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Permessi account */}
      <div>
        <h2 className="text-lg font-semibold mb-1">Permessi account email</h2>
        <p className="text-sm text-gray-500 mb-4">
          Spunta gli account email che ogni dipendente può vedere. L'admin vede sempre tutto.
        </p>

        {accounts.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400 rounded-xl border" style={{ borderColor: '#E8EAED' }}>
            Nessun account email configurato. Vai prima alla tab "Account" e aggiungi le caselle.
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400 rounded-xl border" style={{ borderColor: '#E8EAED' }}>
            Nessun dipendente da configurare.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border" style={{ borderColor: '#E8EAED' }}>
            <table className="w-full text-sm">
              <thead style={{ background: '#FAFAF9' }}>
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600">Account email</th>
                  {users.map(u => (
                    <th key={u.id} className="text-center p-3 font-semibold text-gray-600 capitalize">
                      {u.username}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map(acc => (
                  <tr key={acc.id} className="border-t" style={{ borderColor: '#F1F3F4' }}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: acc.color }} />
                        <span className="truncate">{acc.email}</span>
                      </div>
                    </td>
                    {users.map(u => {
                      const key = `${u.id}-${acc.id}`;
                      const allowed = isAllowed(u.id, acc.id);
                      const saving = savingKey === key;
                      return (
                        <td key={u.id} className="p-3 text-center">
                          <button
                            data-testid={`perm-toggle-${u.username}-${acc.id}`}
                            onClick={() => togglePerm(u.id, acc.id)}
                            disabled={saving}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-all"
                            style={{
                              background: allowed ? '#E2725B' : '#F1F3F4',
                              color: allowed ? 'white' : '#9CA3AF',
                              border: `1px solid ${allowed ? '#E2725B' : '#E8EAED'}`,
                              cursor: saving ? 'wait' : 'pointer',
                            }}
                            title={allowed ? 'Clicca per revocare' : 'Clicca per abilitare'}
                          >
                            {saving
                              ? <Loader2 size={12} className="animate-spin" />
                              : allowed ? <Check size={14} /> : <X size={14} />
                            }
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cambio password */}
      <div>
        <h2 className="text-lg font-semibold mb-1">Cambia password utente</h2>
        <p className="text-sm text-gray-500 mb-4">
          Imposta una nuova password per uno dei due account di accesso.
        </p>

        <form onSubmit={handleChangePassword} className="space-y-3 p-4 rounded-xl border" style={{ borderColor: '#E8EAED', background: 'white' }}>
          <div className="flex gap-2 p-1 rounded-lg" style={{ background: '#F1F3F4' }}>
            <button
              type="button"
              data-testid="pwd-tab-admin"
              onClick={() => setPwUser('admin')}
              className="flex-1 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-all"
              style={{
                background: pwUser === 'admin' ? 'white' : 'transparent',
                color: pwUser === 'admin' ? '#333' : '#7F8C8D',
                boxShadow: pwUser === 'admin' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
              }}
            >Admin</button>
            <button
              type="button"
              data-testid="pwd-tab-ufficio"
              onClick={() => setPwUser('ufficio')}
              className="flex-1 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-all"
              style={{
                background: pwUser === 'ufficio' ? 'white' : 'transparent',
                color: pwUser === 'ufficio' ? '#333' : '#7F8C8D',
                boxShadow: pwUser === 'ufficio' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
              }}
            >Ufficio</button>
          </div>

          <div className="relative">
            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="pwd-new-input"
              type={pwShow ? 'text' : 'password'}
              value={pwNew}
              onChange={e => setPwNew(e.target.value)}
              placeholder={`Nuova password per "${pwUser}" (min 6 caratteri)`}
              className="w-full pl-10 pr-10 py-2.5 text-sm rounded-lg border focus:outline-none"
              style={{ borderColor: '#E8EAED' }}
            />
            <button
              type="button"
              onClick={() => setPwShow(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {pwShow ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>

          <button
            type="submit"
            data-testid="pwd-save-btn"
            disabled={pwSaving || !pwNew}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white rounded-lg transition-all"
            style={{
              background: '#E2725B',
              opacity: pwSaving || !pwNew ? 0.6 : 1,
              cursor: pwSaving || !pwNew ? 'not-allowed' : 'pointer',
            }}
          >
            {pwSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {pwSaving ? 'Salvataggio...' : `Aggiorna password ${pwUser}`}
          </button>
        </form>
      </div>
    </div>
  );
}
